import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Roles, useAuth } from "../providers/AuthContext";
import CustomDropdown from "../components/CustomDropdown";
import LanguageSwitcher from "./LanguageSwitcher";
import { getAllOrganizations } from "../api/Resources";
import {
    getActingOrganizationPublicId,
    setActingOrganizationPublicId,
} from "../helpers/actingOrganization";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPowerOff, faBars } from "@fortawesome/pro-regular-svg-icons";
import { useSidebar } from "../providers/SidebarContext";
import { Link } from "react-router-dom";

function getInitials(name = "") {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return "?";
    }
    if (parts.length === 1) {
        return (parts[0][0] || "?").toUpperCase();
    }
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function splitFirstAndLastName(name = "") {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return { firstName: "", lastName: "" };
    }
    if (parts.length === 1) {
        return { firstName: parts[0], lastName: "" };
    }
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

const containerClass =
    "relative z-50 flex h-[70px] items-center justify-between border-b px-4 shadow-sm md:px-6 lg:px-8";

const menuClass =
    "flex min-w-[12rem] flex-col rounded-xl border border-mute-100 bg-white p-2 shadow-xl ring-1 ring-black/5";

const avatarButtonClass =
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary-200/80 " +
    "bg-primary-200 text-sm font-bold text-primary-800 shadow-sm ring-2 ring-white transition-transform " +
    "hover:border-primary-300 hover:bg-primary-400";

function HeaderBar() {
    const { t } = useTranslation();
    const { user, logout, hasRole, getAccessToken } = useAuth();
    const { toggleSidebar } = useSidebar();
    const [organizations, setOrganizations] = useState([]);
    const actingOrganizationPublicId = getActingOrganizationPublicId();

    const userName = user?.name || "";
    const initials = getInitials(userName);
    const { firstName, lastName } = splitFirstAndLastName(userName);
    const isAdmin = hasRole(Roles.Admin);

    useEffect(() => {
        if (!isAdmin) {
            return;
        }

        let cancelled = false;
        const loadOrganizations = async () => {
            try {
                const token = await getAccessToken();
                const result = await getAllOrganizations(token);
                if (!cancelled) {
                    const list = (Array.isArray(result) ? result : [])
                        .filter((organization) => organization.isDefault === true || organization.isCpms === true);
                    setOrganizations(
                        [...list].sort((a, b) => {
                            if (a.isDefault === b.isDefault) {
                                return (a.organizationName || "").localeCompare(b.organizationName || "");
                            }
                            return a.isDefault ? -1 : 1;
                        })
                    );
                }
            } catch (error) {
                console.error(error);
            }
        };

        loadOrganizations();
        return () => {
            cancelled = true;
        };
        // getAccessToken is stable enough for this load; including it retriggers every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const actingOrganization = organizations.find((organization) =>
        actingOrganizationPublicId
        && organization.organizationPublicId === actingOrganizationPublicId
        && organization.isDefault !== true
    );

    const selectOrganization = (organization) => {
        if (organization.isDefault) {
            setActingOrganizationPublicId("");
        } else {
            setActingOrganizationPublicId(organization.organizationPublicId);
        }
        window.location.reload();
    };

    return (
        <div className={`${containerClass} border-mute-200 bg-white`}>
            <div className="relative z-10 flex min-w-0 flex-1 items-center gap-2 md:gap-4">
                <button
                    type="button"
                    onClick={toggleSidebar}
                    className="-ml-1 rounded-lg p-2 text-mute-600 transition-colors hover:bg-primary-50 hover:text-primary-700 md:hidden"
                    aria-label={t("header.toggleSidebar")}
                >
                    <FontAwesomeIcon icon={faBars} className="text-xl" />
                </button>

                <div className="logoContainer flex min-w-0 items-center gap-3 py-1 md:gap-4">
                    <Link
                        to="/"
                        className="group/logo inline-flex shrink-0 items-center rounded-lg px-2 py-1.5 transition-opacity hover:opacity-95"
                        aria-label={t("header.home")}
                    >
                        <img
                            src="/logo/gito_logo.svg"
                            alt="GITO"
                            className="h-9 w-auto origin-left transition-transform duration-200 group-hover/logo:scale-[1.02] md:h-10"
                        />
                    </Link>
                    <span
                        className="h-10 w-px shrink-0 bg-primary-200 sm:block md:h-11"
                        aria-hidden="true"
                    />
                    <span className="inline-block cursor-default select-none font-sans text-xl font-bold leading-tight tracking-normal text-primary-800 md:text-2xl">
                        Servo
                    </span>
                    {actingOrganization ? (
                        <span className="truncate rounded-full bg-kraft-500 px-3 py-1 text-sm font-bold text-primary-800">
                            {t("header.actingAs", { organization: actingOrganization.organizationName })}
                        </span>
                    ) : null}
                </div>
            </div>

            <div className="accountContainer relative z-[100] flex shrink-0 items-center gap-3">
                {!userName ? <LanguageSwitcher /> : null}
                {userName ? (
                    <CustomDropdown
                        isRight={true}
                        ContentComponent={() => (
                            <div className={menuClass}>
                                <div className="mb-2 border-b border-mute-100 px-3 pb-3 pt-1">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-mute-400">
                                        {t("header.loggedInAs")}
                                    </p>
                                    <div className="mt-1.5 flex flex-col gap-0.5">
                                        {firstName ? (
                                            <p className="text-sm font-bold ">{firstName} {lastName}</p>
                                        ) : null}
       
                                        {!firstName && !lastName ? (
                                            <p className="text-sm font-semibold text-mute-500">—</p>
                                        ) : null}
                                    </div>
                                    {isAdmin && organizations.length > 0 ? (
                                        <>
                                            <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-mute-400">
                                                {t("entity.organization")}
                                            </p>
                                            <div className="mt-1.5 flex flex-col">
                                                {organizations.map((organization) => {
                                                    const isSelected = organization.isDefault
                                                        ? !actingOrganizationPublicId
                                                        : actingOrganizationPublicId === organization.organizationPublicId;
                                                    return (
                                                        <button
                                                            key={organization.organizationPublicId}
                                                            type="button"
                                                            className={`rounded-lg px-2 py-1.5 text-left text-sm ${isSelected ? "font-bold text-primary-800" : "font-semibold text-mute-700 hover:bg-primary-50"}`}
                                                            onClick={() => selectOrganization(organization)}
                                                        >
                                                            {organization.organizationName}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : null}
                                    <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-mute-400">
                                        {t("header.language")}
                                    </p>
                                    <div className="mt-1.5">
                                        <LanguageSwitcher />
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="flex w-full items-center rounded-lg py-2.5 px-3 text-left text-sm font-semibold text-mute-700 transition-colors hover:bg-primary-50 hover:text-primary-800"
                                    onClick={logout}
                                >
                                    <FontAwesomeIcon icon={faPowerOff} className="mr-2 w-4 text-mute-500" />
                                    {t("header.logout")}
                                </button>
                            </div>
                        )}
                    >
                        <button
                            type="button"
                            className={avatarButtonClass}
                            aria-label={t("header.account", { name: userName })}
                            title={userName}
                        >
                            {initials}
                        </button>
                    </CustomDropdown>
                ) : null}
            </div>
        </div>
    );
}

export default HeaderBar;
